import { Component, signal, OnInit, inject } from '@angular/core';
import { RouterLink } from '@angular/router';
import { BeforeAfterComponent } from '../../shared/components/before-after/before-after.component';
import { InlinePromotionComponent } from '../../shared/components/inline-promotion/inline-promotion.component';
import { ScrollAnimateDirective } from '../../shared/directives/scroll-animate.directive';
import { SeoService } from '../../core/services/seo.service';
import { SiteConfigService } from '../../core/config/site-config.service';
import { ParticlesComponent } from '../../shared/components/particles/particles.component';
@Component({
  selector: 'app-projects',
  standalone: true,
  imports: [RouterLink, BeforeAfterComponent, InlinePromotionComponent, ScrollAnimateDirective, ParticlesComponent],
  template: `
    <!-- Hero -->
    <section class="relative py-24 md:py-32 -mt-20 pt-32 md:pt-40 bg-surface-950 overflow-hidden grain-overlay">
      <div class="absolute inset-0 bg-gradient-to-br from-brand-950 via-surface-950 to-brand-900"></div>
      <app-particles class="absolute inset-0 z-[1] pointer-events-none" [count]="50" [connectionDistance]="130" [mouseRadius]="180" />
      <div class="mx-auto max-w-7xl px-6 lg:px-8 relative z-10">
        <div class="max-w-2xl">
          <div class="section-label section-label-light">{{ page.hero.label }}</div>
          <h1 class="font-heading text-4xl md:text-5xl font-extrabold text-white leading-tight tracking-tight mb-5">
            {{ page.hero.title }} <span class="text-brand-300">{{ page.hero.highlight }}</span>
          </h1>
          <p class="text-brand-200 leading-relaxed">{{ page.hero.description }}</p>
        </div>
      </div>
    </section>

    <!-- Filters -->
    <section class="py-6 bg-white border-b border-surface-100 sticky top-[68px] z-30">
      <div class="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div class="flex flex-wrap gap-2">
          @for (cat of categories; track cat) {
            <button (click)="activeFilter.set(cat)"
                    class="px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200"
                    [class]="activeFilter() === cat ? 'bg-brand-600 text-white' : 'bg-surface-100 text-surface-500 hover:bg-surface-200'">
              {{ cat }}
            </button>
          }
        </div>
      </div>
    </section>

    <!-- Gallery Grid -->
    <section class="py-16 md:py-24 bg-white">
      <div class="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          @for (p of filteredProjects(); track p.title; let i = $index) {
            <a [routerLink]="'/projects/' + p.slug" appScrollAnimate data-animate
               class="group rounded-xl overflow-hidden bg-white shadow-sm hover:shadow-xl hover:shadow-brand-600/[0.08] transition-all duration-400 hover:-translate-y-1">
              <div class="aspect-[4/3] overflow-hidden relative">
                <img [src]="p.image" [alt]="p.title" class="w-full h-full object-cover group-hover:scale-[1.06] transition-transform duration-500" />
                <div class="absolute inset-0 bg-gradient-to-t from-surface-950/60 via-surface-950/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-400"></div>
                <div class="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-400">
                  <span class="inline-flex items-center gap-2 px-5 py-2.5 border border-white/80 text-white text-sm font-semibold rounded-lg backdrop-blur-sm bg-white/10 translate-y-3 group-hover:translate-y-0 transition-transform duration-400">
                    View Project
                    <svg aria-hidden="true" class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" /></svg>
                  </span>
                </div>
              </div>
              <div class="p-5">
                <span class="text-[0.65rem] font-bold uppercase tracking-wider text-brand-600">{{ p.category }}</span>
                <h3 class="font-heading text-base font-bold text-surface-900 mt-1 mb-1 group-hover:text-brand-700 transition-colors">{{ p.title }}</h3>
                <p class="text-sm text-surface-500">{{ p.location }}</p>
              </div>
            </a>
          }
        </div>
      </div>
    </section>

    <!-- Promotion -->
    @if (projectsPromo) {
      <app-inline-promotion [promo]="projectsPromo" />
    }

    <!-- Before & After Gallery -->
    <section class="py-20 md:py-28 bg-surface-50">
      <div class="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div class="text-center mb-14" appScrollAnimate data-animate>
          <div class="section-label justify-center">Before & After</div>
          <h2 class="font-heading text-3xl md:text-4xl font-bold text-surface-900 tracking-tight">See the Transformation</h2>
        </div>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-8">
          @for (item of beforeAfters; track item.title; let i = $index) {
            <div appScrollAnimate [attr.data-animate]="''" [attr.data-delay]="(i+1)*150">
              <app-before-after [beforeImage]="item.before" [afterImage]="item.after" aspectRatio="16/10" />
              <h4 class="font-heading text-base font-bold text-surface-900 mt-4">{{ item.title }}</h4>
              <p class="text-sm text-surface-500">{{ item.location }}</p>
            </div>
          }
        </div>
      </div>
    </section>

    <!-- CTA -->
    <section class="py-16 bg-brand-600">
      <div class="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 text-center">
        <h2 class="font-heading text-2xl md:text-3xl font-bold text-white mb-5">Like What You See?</h2>
        <p class="text-brand-100 mb-7 max-w-lg mx-auto">Let's add your project to our portfolio. Get a free estimate today.</p>
        <a routerLink="/contact" class="inline-flex items-center gap-2 px-7 py-3.5 bg-white text-brand-700 font-semibold text-sm rounded-lg hover:bg-brand-50 transition-colors">
          Get Free Estimate
          <svg aria-hidden="true" class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" /></svg>
        </a>
      </div>
    </section>
  `,
})
export class ProjectsComponent implements OnInit {
  private seo = inject(SeoService);
  private config = inject(SiteConfigService);

  page = this.config.pages.projects;
  categories = this.page.categories;
  beforeAfters = this.page.beforeAfters;
  projectsPromo = this.config.promotions.projectsInline;

  activeFilter = signal('All');

  filteredProjects = () => {
    const f = this.activeFilter();
    return f === 'All' ? this.page.projects : this.page.projects.filter(p => p.category === f);
  };

  ngOnInit(): void {
    this.seo.setJsonLd(this.seo.getBreadcrumbSchema([
      { name: 'Home', url: '/' },
      { name: 'Projects', url: '/projects' },
    ]));
  }
}
